import React, { useEffect, useState } from "react";
import TeamTable from "./components/TeamTable";
import { Box } from "@chakra-ui/react";
import { createAPIEndPoint } from "config/api/api";
import toast from "react-hot-toast";
import Loader from "loaders/Loader";
import { logoutUser, getUserData } from "utils";
import { useNavigate } from "react-router-dom";

const Team = () => {
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("access_token") || null);

  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? null;

  // ✅ Step 1: Extract token from URL and clean it
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get("token");

    if (tokenFromUrl) {
      localStorage.setItem("access_token", tokenFromUrl);
      setToken(tokenFromUrl);

      // 🔹 Clean URL (remove token param)
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      console.log("🔐 Token set from URL:", tokenFromUrl);
    }
  }, []);

  // ✅ Step 2: Validate token and load auth profile
  useEffect(() => {
    if (!token) return;

    const checkAuthProfile = async () => {
      try {
        const res = await createAPIEndPoint("auth_profile").fetchAll();
        const data = res.data;

        localStorage.setItem("user_profile", JSON.stringify(data.profile));
        localStorage.setItem("user_role", JSON.stringify(data.profile.user_role));

        console.log("✅ Auth Profile fetched:", data);

        // ✅ after saving profile, use the correct clinic_id from response
        const fetchedClinicId = data.profile?.clinic_id;
        if (fetchedClinicId) {
          fetchTeam(fetchedClinicId);
        }
      } catch (error) {
        console.error("Auth profile error:", error);
        const msg = error?.response?.data?.error || "";
        if (msg.includes("Invalid Bearer token") || msg.includes("Unauthorized")) {
          toast.error("Session expired. Please log in again.");
          logoutUser(navigate);
        }
      }
    };

    checkAuthProfile();
  }, [token]);

  // ✅ Step 3: Fetch team data when authenticated and clinic_id is ready
  // const fetchTeam = async (clinicIdParam) => {
  //   const id = clinicIdParam || clinic_id;
  //   if (!id) return;

  //   // setLoading(true);
  //   const controller = new AbortController();
  //   const timeoutId = setTimeout(() => controller.abort(), 10000);

  //   try {
  //     const response = await createAPIEndPoint(`clinic_team/get_all/${id}`).fetchAll({
  //       signal: controller.signal,
  //     });
  //     setTeamData(response.data.team_members || []);
  //     console.log("👥 Team members fetched:", response.data.team_members);
  //   } catch (err) {
  //     if (err.name === "AbortError") {
  //       toast.error("Request timed out. Please try again.");
  //     } else {
  //       const msg = err?.response?.data?.error || "Error fetching team";
  //       toast.error(msg);
  //       if (msg.includes("Bearer token")) logoutUser(navigate);
  //     }
  //   } finally {
  //     clearTimeout(timeoutId);
  //     setLoading(false);
  //   }
  // };

  const fetchTeam = async (clinicIdParam) => {
  const id = clinicIdParam || clinic_id;
  if (!id) return;

  setLoading(true);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await createAPIEndPoint(
      `clinic_team/get_all/${id}`
    ).fetchAll({
      signal: controller.signal,
    });

    const teamMembers = response.data.team_members || [];

    // Latest first by created_at
    const sortedTeamMembers = teamMembers.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    setTeamData(sortedTeamMembers);

    console.log("👥 Team members fetched:", sortedTeamMembers);
  } catch (err) {
    if (err.name === "AbortError") {
      toast.error("Request timed out. Please try again.");
    } else {
      const msg =
        err?.response?.data?.error || "Error fetching team";

      toast.error(msg);

      if (msg.includes("Bearer token")) {
        logoutUser(navigate);
      }
    }
  } finally {
    clearTimeout(timeoutId);
    setLoading(false);
  }
};

  // ✅ Step 4: Re-fetch team when local clinic_id changes
  useEffect(() => {
    if (clinic_id && token) {
      const delay = setTimeout(() => {
        fetchTeam(clinic_id);
      }, 300); // short delay lets token sync to axios
      return () => clearTimeout(delay);
    }
  }, [clinic_id, token]);

  return (
    <Box pt={{ base: "80px", md: "80px", xl: "80px" }}>
      {loading && <Loader />}
      <TeamTable tableData={teamData} loading={loading} fetchAgain={fetchTeam} />
    </Box>
  );
};

export default Team;
